import { MatchKeysAndValues } from "../../../@components/interfaces.js";
import { Simera } from "../../../@components/simera/simera.js";
import { getLang } from "../../../@strings/strings.js";
import { OfflineDexie } from "../../../apps/offline.ct.gt/db/offline-dexie.js";
import { HighPerformance } from "../../../tests/response/high-performance/high-performance.js";
import { IStatePerformTestPut } from "../../../tests/response/models/initial-state-test-response";
import { ReportDatav2 } from "../../../tests/results/models/report-response";
import { ListTestCompleteResults } from "../../../tests/results/models/users-results-list-complete";
import { naturalSort } from "../../../xconfig/utils.js";
import { CtResponse, IPagination, toQueryString, zget } from "../Fetch";
import { getAPIURLv2, xFetch, xget, xhttp } from "../Fetchv2.js";
import { MGST } from "../schemas/mg.schema-types.js";
import { MGSchema, TestQuestion, tests_complete } from "../schemas/mg.schema.js";
import { PGSchema } from "../schemas/pg.schema.js";
import { SR } from "../schemas/schemas/schema-reponses.js";
import { CombinedReports } from "./combined-reports/combined-reports.js";
import { AssessmentsFolders } from "./folders.js";

class Monitoring {
	async deleteTest(pgid: PGSchema.pages["id"], _id: tests_complete["_id"], token: string) {
		const tsup = await xhttp<{ tests: tests_complete[] }>(`/pages/${pgid}/testsbyid/${_id}`, "DELETE", { token });
		return tsup;
	}

	async getUsersForMonotoring() {
		const tsup = await xget<{ tests: tests_complete[] }>(`/monitoring`);
		return tsup;
	}

	/**
	 * Get-or-create the Cloudflare RealtimeKit meeting used to monitor a test
	 * session. All monitors + test-takers of the same `(pgid, group)` share the
	 * same meeting id so we don't over-allocate RTK rooms.
	 */
	async getOrCreateMonitoringRoom(pgid: string, group: string) {
		const response = await xhttp<{ meetingId: string }>(`/pages/${pgid}/monitoring-room`, "POST", { group });
		return response;
	}

	/**
	 * Add this user to the RealtimeKit meeting with the right preset and get
	 * back the short-lived `authToken` the SDK needs to join.
	 */
	async addMonitoringParticipant(pgid: string, meetingId: string, body: { name: string; role: "monitor" | "taker"; customParticipantId: string }) {
		const response = await xhttp<{ authToken: string }>(`/pages/${pgid}/monitoring-room/${meetingId}/participant`, "POST", body);
		return response;
	}
}
export class Tests {
	monitoring = new Monitoring();
	folders = new AssessmentsFolders();

	/**
	 * @deprecated
	 */
	async getUser(uid: string): Promise<CtResponse<[{ name: string; last_name: string; username: string; thumb_pp: string; pp: string; email: string }]>> {
		let response = await zget(`/pages/${uid}/user`);
		return response;
	}

	async getVerify(_id: string) {
		let response = await xget<{ user: PGSchema.users; test: tests_complete }>(`/verify/test/${_id}`);
		return response;
	}

	async requalifyTestComplete(pgid: PGSchema.pages["id"], _id: tests_complete["_id"]) {
		let pginfo = await window.app.pages.getToken();
		let response = await xhttp<{ ok: number }>(`/pages/${pgid}/tests_complete/requalify`, "PUT", { _id, pgid }, { headers: { "app-token": pginfo?.token! } });
		return response;
	}

	async getTestsByPage(uid: string, queryParams: IPagination & { type?: MGST.test.type; by?: string; sub?: string; folder_id?: PGSchema.tests_folders["id"] }, token: string) {
		let body = { body: JSON.stringify(queryParams) };
		let response = await xget<{ data: SR.apiv2.test.getTestsByPage[]; folders: PGSchema.tests_folders[] }>(`/pages/${uid}/tests`, body, { headers: { "app-token": token! } });

		response.json.folders = response.json.folders.map(f => ({ ...f, psm_child_folders: response.json.folders.filter(f2 => f2.parent == f.id).sort((a, b) => naturalSort(a.name, b.name)) }));
		return response;
	}

	async createAssessment(uid: string, token: string, ob: MGSchema.tests) {
		let response = await xhttp<{ ok: number; _id: string; v: number; errors: any[] }>(`/pages/${uid}/tests`, "PUT", { test: { ...ob, lang: getLang() } }, { headers: { "app-token": token! } });
		return response;
	}

	/**
	 * @ADMIN
	 */
	async editPageTest(uid: string, token: string, ob: MatchKeysAndValues<MGSchema.tests>, delete_inactive?: boolean): Promise<CtResponse<{ ok: number; _id: string; v: number; errors: any[] }>> {
		let response = await xhttp(`/pages/${uid}/tests/${ob.tid}`, "PUT", { test: { ...ob }, token, delete_inactive });
		return { response: response.response, json: response.json } as any;
	}

	/**
	 * @ADMIN
	 */
	async requalifyTest(uid: string, token: string, ob: MGSchema.tests): Promise<CtResponse<{ ok: number; _id: string; v: number; errors: any[] }>> {
		let response = await xhttp(`/pages/${uid}/tests/${ob._id}/requalify`, "POST", { test: { ...ob }, token });
		return { response: response.response, json: response.json } as any;
	}

	async deletePagesTest(uid: string, id: string, tid: number, v: number): Promise<CtResponse<{ ok: number | undefined }>> {
		const tsdel = await xhttp(`/pages/${uid}/tests`, "DELETE", { uid, id, tid, v });
		return tsdel;
	}

	async getPageTest(pgid: PGSchema.pages["id"], tid: number, id?: string, v?: number, token?: string) {
		const response = await xget<MGSchema.tests>(`/pages/${pgid}/tests/${tid}`, { id, v }, { headers: token ? { "app-token": token } : {} });
		return response;
	}
	/**
	 * Este metodo devuelve un listado de "groups" que estan realizando pruebas en la pagina
	 */
	async getTestGroupsToMonitor(pgid: string, token: string) {
		const response = await xget<{ data: { _id: string; doc_count: number }[] }>(`/pages/${pgid}/monitor`, { token });
		return response;
	}

	async getTestsOffline(uid: string, tid: number[], token?: string) {
		const response = await xget<{ tests: MGSchema.tests[] }>(`/pages/${uid}/tests_to_offline`, { token, tid: tid.join(",") });
		return response;
	}

	async updatePageTest(uid: string, tid: number, ob: any): Promise<CtResponse<any>> {
		let response = await xhttp(`/pages/${uid}/tests/${tid}`, "PUT", ob);
		return response;
	}

	types!: {
		assignTestToUser: {
			/** Token de la pag */
			token: string;
			emails?: string[];
			uids?: string[];
			/** PP de la pagina */
			pic?: PGSchema.pages["thumb_pp"];
			/** Nombre de la pag que envia el test */
			name?: string;
			/** Si le quiere enviar un mensaje en el correo */
			message?: string;

			start_date?: number;
			finish_date?: number;
			/** tid del testNormal que se va a enviar */
			tid: number;
			limit_time?: number;
			hp?: boolean;
			preferences?: MGST.test.preferences[];
			tags?: string[];
			lang: string | undefined;
			profileID?: MGSchema.profiles["_id"];
			/** Esto es para saber si un grupo de usuarios, todos finalizaron las pruebas que les han asignado en conjunto para generar un informe */
			assignID?: string;
			/** Quien asigno la prueba - Solo para `autoassignTestToUser` */
			assign_by?: string;
		};
	};
	async autoassignTestToUser(
		params: { tid: MGSchema.tests["tid"][]; referrer?: string; href?: string; ref?: string } & Pick<MGSchema.tests, "uid"> &
			Pick<PGSchema.users, "name" | "last_name" | "email" | "dpi"> &
			Pick<typeof this.types.assignTestToUser, "tags" | "profileID" | "assignID" | "assign_by" | "lang">
	) {
		let response = await xhttp<{ token: string; already_assigned?: boolean }>(`/pages/${params.uid}/tests/autoassign`, "POST", params);
		return response;
	}

	async assignTestToUser(uid: string, ob: typeof this.types.assignTestToUser) {
		let response = await xhttp<{ ok: number; errors: string }>(`/pages/${uid}/tests/assign`, "POST", ob);
		return response;
	}

	async reenable(_id: string, limit_time?: number, blur_count?: number) {
		const tsup = await xhttp<{ c: number }>(`/pages/:uid/tests/:tid/users/${_id}/reenable`, "PUT", { limit_time, blur_count });
		return tsup;
	}

	async restart(pgid: string, tid: number, _id: string, limit_time?: number, blur_count?: number) {
		const tsup = await xhttp<{ c: number }>(`/pages/${pgid}/tests/${tid}/users/${_id}/restart`, "PUT", { limit_time, blur_count });
		return tsup;
	}

	/** Obtiene los resultados de pruebas para un TID */
	async getAllTestAssigned(
		pgid: string,
		body?: { tid?: number; type?: number; uid?: string; skip?: number; limit?: number; complete?: boolean; scores?: number[]; email?: string; dpi?: string },
		token?: string
	) {
		const users = await xhttp<ListTestCompleteResults>(`/pages/${pgid}/tests/assigned/users`, "POST", body, { headers: token ? { "app-token": token } : {} });
		return users;
	}

	/** Obtiene los resultados de un usuario */
	async getTestOfUser(pgid: string, uid: string, { skip, limit }: { skip?: number; limit?: number }): Promise<CtResponse<ListTestCompleteResults>> {
		const users = await zget(`/pages/${pgid}/tests/assigned/users/${uid}`, { skip, limit });
		return users;
	}

	/**
	 * Edita varios test completo
	 * @GET /pages/:uid/tests_complete
	 */
	async getTestComplete(pgid: string, token: string, query: MatchKeysAndValues<Pick<tests_complete, "_id" | "pgid" | "uid"> & { tid: any }>) {
		const tsdel = await xget<{ tests: tests_complete[]; users: PGSchema.users[] }>(`/pages/${pgid}/tests_complete`, { query: JSON.stringify(query) }, { headers: { "app-token": token } });
		return tsdel;
	}

	/**
	 * Edita varios test completo
	 * @PATCH /pages/:uid/tests_complete
	 */
	async editTestComplete(pgid: string, token: string, query: MatchKeysAndValues<Pick<tests_complete, "_id" | "tid" | "pgid" | "uid"> & { tags: any }>, body: MatchKeysAndValues<tests_complete>) {
		const tsdel = await xhttp<{ modifiedCount: number }>(`/pages/${pgid}/tests_complete`, "PATCH", { query: query, body: body }, { headers: { "app-token": token } });
		return tsdel;
	}

	/**
	 * Eliminia un test completo
	 * @DELETE /pages/:uid/tests_complete
	 */
	async deleteTestComplete(pgid: string, tid: number, _id: string, v: number, uid: string) {
		return xhttp<{ ok: number | undefined }>(`/pages/${pgid}/tests_complete`, "DELETE", [{ _id, tid, uid }]);
	}

	/**
	 * Eliminia varios test completo
	 * @DELETE /pages/:uid/tests_complete
	 */
	async deleteManyTestComplete(pgid: string, params: Pick<tests_complete, "_id" | "tid" | "uid">[]) {
		return xhttp<{ ok: number | undefined }>(`/pages/${pgid}/tests_complete`, "DELETE", params);
	}

	/**
	 * @GET /pages/:uid/tests/:tid/results/:_id
	 * @GET /pages/:uid/tests/:tid/users/:_id/resultsv2
	 */
	async getUserResultv2(pgid: string, tid: string | number, _id: string, token?: string) {
		// const users = await zget<ReportDatav2>(`/pages/${pgid}/tests/${tid}/results/${_id}`, { v });
		const users = await xget<ReportDatav2>(`/pages/${pgid}/tests/${tid}/users/${_id}/resultsv2`, {}, { headers: token ? { "app-token": token } : {} });
		return users;
	}

	async getTestStats(pgid: string, tid: string, token: string) {
		const users = await xget<{ template: MGSchema.tests; total: number; stats: { group: string; question: number; answer: number; count: number }[] }>(
			`/pages/${pgid}/tests/${tid}/stats`,
			{},
			{ headers: { "app-token": token } }
		);
		return users;
	}

	async getShortLinkOfTest(tid: number, pgid: string, type: any, name: string): Promise<CtResponse</* set_of_assessments */ any>> {
		const users = await xhttp(`/pages/${pgid}/soa`, "POST", { tid, pgid, type, name });
		return users;
	}
	async updateShortLinkOfTest(tid: number, pgid: string, type: any, name: string): Promise<CtResponse</* set_of_assessments */ any>> {
		const users = await xhttp(`/pages/${pgid}/soa`, "PUT", { tid, pgid, type, name });
		return users;
	}

	// Users
	async setAnswersOnQuestion(pgid: string, _id: string, tid: number, v: number, params: TestQuestion): Promise<[IStatePerformTestPut, xFetch.xError<null> | undefined]> {
		let hp = HighPerformance.getInstance();
		if (_id.startsWith("~") && hp.testComplete != null) {
			return hp.t0EvalNormal({ questionAnswered: params });
		} else {
			const [rsp, err] = await xhttp<IStatePerformTestPut, null>(`/users/me/tests/${_id}`, "PUT", { params, tid, pgid, v });
			return [rsp, err];
		}
	}

	// Califica las preguntas que el usuario responda
	async skipQuestion(pgid: string, _id: string, tid: number, v: number, params: TestQuestion) {
		let hp = HighPerformance.getInstance();
		if (_id.startsWith("~") && hp.testComplete != null) {
			await hp.skipQuestion(_id, params);
			return [{ ok: 1 }];
		}
		const [rsp, err] = await xhttp<IStatePerformTestPut>(`/users/me/tests/${_id}/skip`, "PUT", { params, tid, pgid, v });
		return [rsp, err];
	}

	// Califica las preguntas que el usuario responda
	async startTest({
		pgid,
		_id,
		tid,
		v,
		cameras,
		device_id,
		geo
	}: {
		pgid: string;
		_id: string;
		tid: number;
		v: number;
		cameras?: { n: string; u: boolean }[];
		device_id?: PGSchema.users_devices["id"];
		geo?: { lat: number; lon: number };
	}) {
		let hp = HighPerformance.getInstance();
		if (hp.mode == "offline" && _id.startsWith("~") && hp.testComplete != null) {
			return [await hp.startTest(_id, cameras), undefined];
		}

		let promiseRsp = xhttp<{ start_time: number; modifiedCount: number }>(`/users/me/tests/${_id}/start`, "PUT", { tid, pgid, v, cameras: cameras || [], device_id, geo });
		if (_id.startsWith("~") && hp.testComplete != null) {
			return [await hp.startTest(_id, cameras), undefined];
		}
		const [rsp, err] = await promiseRsp;
		return await promiseRsp;
	}

	//

	async setCompleteTest(params: { _id: string; testType: number; pgid: string; tid: number; v: number; complete_msg?: MGST.tests_complete.complete_msg; timeover_details?: any }) {
		let hp = HighPerformance.getInstance();
		debugger;
		if (params._id.startsWith("~") && hp.testComplete != null) {
			let testComplete = await hp.setCompleteTest(params);
			if (testComplete == null) return;
			hp.cleanTestComplete();
			if (hp.mode == "firestore") {
				const [tsup, err] = await xhttp<{ ok: number }>(
					`/users/me/tests/${params._id}/complete-hp`,
					"PUT",
					{
						test_type: params.testType,
						pgid: params.pgid,
						tid: params.tid,
						v: params.v,
						complete_msg: params.complete_msg,
						timeover_details: params.timeover_details
					},
					{
						retries: 1000
					}
				);
			} else if (hp.mode == "offline") {
				let offlineDB = new OfflineDexie();
				await OfflineDexie.ready;
				await offlineDB.saveTestComplete(testComplete);
			}
		} else {
			const tsup = await xhttp<{ ok: number }>(`/users/me/tests/${params._id}/complete`, "PUT", {
				test_type: params.testType,
				pgid: params.pgid,
				tid: params.tid,
				v: params.v,
				complete_msg: params.complete_msg,
				timeover_details: params.timeover_details
			});
		}
	}

	async onBlurTest(_id: string, testType: number, pgid: string, tid: number, v: number): Promise<[{ c: number }, any]> {
		let hp = HighPerformance.getInstance();
		if (_id.startsWith("~") && hp.testComplete != null) {
			let c = await hp.blurTest(_id);
			return [{ c: c }, undefined];
		} else {
			const [rsp, err] = await xhttp<{ c: number }>(`/users/me/tests/${_id}/blur`, "PUT", { test_type: testType, pgid, tid, v });
			return [rsp, err];
		}
	}

	async addScreenshot(_id: string, params: { photo?: string; dpi?: string; headshots?: string[]; screenshot?: string; cam_stream?: string; screen_stream?: string }, size: number, pgid: string) {
		const tsup = xhttp<{ c: number }>(`/users/me/tests/${_id}/s`, "PUT", { ...params, size, pgid });
		let hp = HighPerformance.getInstance();
		if (_id.startsWith("~") && hp.testComplete != null) {
			let count = await hp.addScreenshot(_id, params);
			return [{ c: count }, undefined];
		}
		return await tsup;
	}
	async getServerTimestamp() {
		const tsup = await xget<{ ts: number }>(`/ts`);
		if (tsup.error) {
			return Date.ts();
		}
		return tsup.json.ts;
	}

	async downloadCSV(pgid: string, mod: string, jid: number, options: { token: string; timezone: number; pipeline: string }) {
		let a = document.createElement("a");
		a.href = getAPIURLv2() + `/pages/${pgid}/${mod}/${jid}/users.xlsx?${toQueryString(options)}`;
		a.target = "_blank";
		a.click();
	}

	// INFORMES

	// OBTENER TESTS POR TIPO
	// /pages/:pgid/test/type/:type
	async getTestsByType(pgid: string, type: number) {
		const res = await zget(`/pages/${pgid}/test/type/${type}`);
		return res;
	}

	// Obtiene los tests-completos por tid's
	async getTestsByTids(pgid: string, profileID: string) {
		const res = await xhttp<{ tests: Omit<tests_complete, "groups">[]; users: PGSchema.users[] }>(`/pages/${pgid}/test/tids`, "SEARCH", { profileID });
		return res;
	}

	// Obtiene los resultados de pruebas (seleccionadas) para un usuario
	async getTestsOfUser(pgid: string, uid: string, tests: number[]) {
		const users = await xhttp<ListTestCompleteResults>(`/pages/${pgid}/tests/assigned/users/${uid}/selected`, "SEARCH", { tests });
		return users;
	}

	// ============================== PREFERENCIAS DE PRUEBAS ==============================
	// Obtiene las preferencias
	async getTestPreferences(pgid: string, token: string) {
		let response = await xget<{ data: PGSchema.page_test_preferences[] }>(`/pages/${pgid}/tests_preferences`, {}, { headers: { "app-token": token } });
		return response;
	}
	// Seteamos las preferencias
	async createTestPreferences(data: Omit<PGSchema.page_test_preferences, "id" | "created_at" | "updated_at">, token: string) {
		let response = await xhttp<{ data: PGSchema.page_test_preferences }>(`/pages/${data.pgid}/tests_preferences/${data.tid}`, "POST", data, { headers: { "app-token": token } });
		return response;
	}

	// Actualiza las preferencias
	async updateTestPreferences(data: Omit<PGSchema.page_test_preferences, "id" | "created_at" | "updated_at">, token: string) {
		let response = await xhttp<{ data: PGSchema.page_test_preferences }>(`/pages/${data.pgid}/tests_preferences/${data.tid}`, "PUT", data, { headers: { "app-token": token } });
		return response;
	}

	// Eliminamos las preferencias
	async deleteTestPreferences(id: number, pgid: string, token: string) {
		let response = await xhttp<{ data: PGSchema.page_test_preferences }>(`/pages/${pgid}/tests_preferences/${id}`, "DELETE", {}, { headers: { "app-token": token } });
		return response;
	}

	async fetchCombinedTestReport(pgid: string, token: string): Promise<[{ reports: any[] }, null]> {
		if (!Simera.isSimeraID(pgid)) return [{ reports: [] }, null];
		return [{ reports: CombinedReports }, null];
	}
}
